import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Chip,
  useTheme,
  Tab,
  Tabs,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import {
  Close,
  GitHub,
  Build,
  Storage,
  Cloud,
  CheckCircle,
  Refresh,
  PlayArrow,
  Error as ErrorIcon,
  Schedule,
  ExpandMore,
  ExpandLess,
  History,
  RocketLaunch,
  Edit as RenameIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Language as DomainIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Star as ShowcaseIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import BindingsPanel from "./BindingsPanel";
import {
  getBuilds, getBuildLogs, forceRebuild, getServiceInstanceLogs,
  getDomainAssignments, getDomains, assignDomainApi, unassignDomainApi,
} from "../../helpers/utils";
import { useSharedProps } from "../../HOC/SharedProps";
import TerminalLog from "../BuildPipeline/TerminalLog";
import MetricsPanel from "../MetricsPanel";
import { API_URL } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";

// Soften kpack's raw status messages — they leak pod-internal phrases
// like "Container export waiting" that mean nothing to an app developer.
// Map the common ones to plain English; anything we don't recognize
// falls through unchanged so we don't hide a useful real error.
const humanizeBuildMessage = (raw) => {
  if (!raw) return raw;
  const s = String(raw);
  if (/container\s+\w+\s+(waiting|pending)/i.test(s)) return "Awaiting build to start…";
  if (/sourceresolver.*not\s*ready/i.test(s)) return "Fetching source from Git…";
  if (/podinitializing/i.test(s)) return "Build pod starting up…";
  if (/imagepullbackoff|errimagepull/i.test(s)) return "Could not pull build image — check ECR credentials.";
  return s;
};

// Build pipeline steps (technology-agnostic)
const BUILD_STEPS = [
  { key: "PREPARE", label: "Prepare", description: "Setting up build environment" },
  { key: "DETECT", label: "Detect", description: "Detecting application type" },
  { key: "ANALYZE", label: "Analyze", description: "Analyzing previous builds" },
  { key: "RESTORE", label: "Restore", description: "Restoring cached layers" },
  { key: "BUILD", label: "Build", description: "Building application" },
  { key: "EXPORT", label: "Export", description: "Exporting container image" },
];

// Animated connection line between stages
const ConnectionLine = ({ active, delay = 0 }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box sx={{ display: "flex", alignItems: "center", mx: 1, width: 60 }}>
      <svg width="60" height="20" viewBox="0 0 60 20">
        <motion.line
          x1="0"
          y1="10"
          x2="50"
          y2="10"
          stroke={isDarkMode ? "#334155" : "#e2e8f0"}
          strokeWidth="2"
        />
        <motion.line
          x1="0"
          y1="10"
          x2="50"
          y2="10"
          stroke="#F97316"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.5, delay }}
        />
        <motion.polygon
          points="50,5 60,10 50,15"
          fill={active ? "#F97316" : isDarkMode ? "#334155" : "#e2e8f0"}
          initial={{ opacity: 0.3 }}
          animate={{ opacity: active ? 1 : 0.3 }}
          transition={{ duration: 0.3, delay: delay + 0.3 }}
        />
      </svg>
    </Box>
  );
};

// Stage card component
const StageCard = ({ icon: Icon, title, subtitle, status, delay, isActive, onClick, duration }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const statusColors = {
    idle: { bg: isDarkMode ? "#1e293b" : "#f8fafc", border: isDarkMode ? "#334155" : "#e2e8f0" },
    active: { bg: isDarkMode ? "#1e3a5f" : "#eff6ff", border: "#F97316" },
    complete: { bg: isDarkMode ? "#14532d" : "#f0fdf4", border: "#22c55e" },
    failed: { bg: isDarkMode ? "#5f1d1d" : "#fef2f2", border: "#ef4444" },
  };

  const colors = statusColors[status] || statusColors.idle;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: "12px",
          background: colors.bg,
          border: `2px solid ${colors.border}`,
          minWidth: 120,
          textAlign: "center",
          position: "relative",
          transition: "all 0.3s ease",
        }}
      >
        {status === "active" && (
          <motion.div
            style={{
              position: "absolute",
              inset: -2,
              borderRadius: "14px",
              border: "2px solid #F97316",
            }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            background: status === "complete"
              ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
              : status === "active"
              ? "linear-gradient(135deg, #F97316 0%, #EA580C 100%)"
              : isDarkMode
              ? "#334155"
              : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 1,
          }}
        >
          {status === "complete" ? (
            <CheckCircle sx={{ color: "#fff", fontSize: "1.5rem" }} />
          ) : status === "active" ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <Refresh sx={{ color: "#fff", fontSize: "1.5rem" }} />
            </motion.div>
          ) : status === "failed" ? (
            <ErrorIcon sx={{ color: "#fff", fontSize: "1.5rem" }} />
          ) : (
            <Icon sx={{ color: isDarkMode ? "#94a3b8" : "#64748b", fontSize: "1.5rem" }} />
          )}
        </Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.85rem",
            color: isDarkMode ? "#e2e8f0" : "#1e293b",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: isDarkMode ? "#64748b" : "#94a3b8",
            mt: 0.5,
          }}
        >
          {subtitle}
        </Typography>
        {/* Elapsed time for this step (kpack build duration, ksvc reconcile).
            Only rendered when we have a real timestamp — never fabricated. */}
        {duration && (
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: status === "complete" ? "#22c55e" : status === "active" ? "#F97316" : status === "failed" ? "#ef4444" : (isDarkMode ? "#64748b" : "#94a3b8"),
              mt: 0.5,
              fontFamily: '"JetBrains Mono", "SF Mono", ui-monospace, monospace',
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            {duration}
          </Typography>
        )}
      </Box>
    </motion.div>
  );
};

// Parse log line to extract timestamp and level
const parseLogLine = (line) => {
  if (typeof line !== 'string') return { timestamp: null, level: null, message: line };

  // Try to match ISO timestamp at start (e.g., 2026-02-04T07:00:50.501Z)
  const isoMatch = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z?)\s+(INFO|WARN|ERROR|DEBUG|TRACE)\s+(.*)$/);
  if (isoMatch) {
    return {
      timestamp: new Date(isoMatch[1]),
      level: isoMatch[2],
      message: isoMatch[3],
    };
  }

  // Try simpler format (e.g., 2026-02-04 07:00:50 INFO ...)
  const simpleMatch = line.match(/^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})\s+(INFO|WARN|ERROR|DEBUG|TRACE)\s+(.*)$/);
  if (simpleMatch) {
    return {
      timestamp: new Date(simpleMatch[1]),
      level: simpleMatch[2],
      message: simpleMatch[3],
    };
  }

  return { timestamp: null, level: null, message: line };
};

// Get color for log level
const getLogLevelColor = (level) => {
  switch (level) {
    case 'ERROR': return '#ef4444';
    case 'WARN': return '#f59e0b';
    case 'DEBUG': return '#8b5cf6';
    case 'TRACE': return '#64748b';
    case 'INFO':
    default: return '#22c55e';
  }
};

// Runtime Panel - shows actual runtime data
const RuntimePanel = ({ runtime, build, onOpenUrl, logs, isLoadingLogs, onRefreshLogs }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [logFilter, setLogFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');

  // Parse and filter logs
  const parsedLogs = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    return logs.map((log, idx) => {
      const line = typeof log === 'string' ? log : log.line;
      return { ...parseLogLine(line), id: idx, raw: line };
    });
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return parsedLogs.filter(log => {
      // Level filter
      if (levelFilter !== 'ALL' && log.level !== levelFilter) {
        return false;
      }
      // Text filter
      if (logFilter && !log.message?.toLowerCase().includes(logFilter.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [parsedLogs, levelFilter, logFilter]);

  const statusColors = {
    running: { bg: "rgba(34, 197, 94, 0.15)", color: "#22c55e", label: "Running" },
    scaled: { bg: "rgba(139, 92, 246, 0.15)", color: "#8b5cf6", label: "Sleeping" },
    deploying: { bg: "rgba(234, 88, 12, 0.15)", color: "#F97316", label: "Deploying" },
    error: { bg: "rgba(239, 68, 68, 0.15)", color: "#ef4444", label: "Error" },
    stopped: { bg: "rgba(100, 116, 139, 0.15)", color: "#64748b", label: "No Service" },
  };

  const status = statusColors[runtime?.status] || statusColors.stopped;

  return (
    <Box>
      {/* Runtime Status Card */}
      <Box
        sx={{
          p: 3,
          borderRadius: "16px",
          background: isDarkMode
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: isDarkMode ? "#e2e8f0" : "#1e293b",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Cloud sx={{ color: "#F97316" }} />
          Runtime Status
        </Typography>

        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {/* Status */}
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              Status
            </Typography>
            <Chip
              size="small"
              icon={
                runtime?.status === "running" || runtime?.status === "scaled" ? (
                  <CheckCircle sx={{ fontSize: "1rem !important", color: `${status.color} !important` }} />
                ) : runtime?.status === "deploying" ? (
                  <CircularProgress size={14} sx={{ color: status.color }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: "1rem !important", color: `${status.color} !important` }} />
                )
              }
              label={status.label}
              sx={{
                background: status.bg,
                color: status.color,
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            />
          </Box>

          {/* Pods */}
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              Active Pods
            </Typography>
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
              {runtime?.pods || 0}
            </Typography>
          </Box>

          {/* CPU */}
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              CPU Usage
            </Typography>
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
              {runtime?.cpu ? `${Math.round((runtime.cpu + Number.EPSILON) * 100) / 100}%` : "0%"}
            </Typography>
          </Box>

          {/* Memory */}
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              Memory
            </Typography>
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
              {runtime?.memory ? `${Math.round(runtime.memory / (1024 * 1024))} MB` : "0 MB"}
            </Typography>
          </Box>
        </Box>

        {/* URL */}
        {runtime?.url && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              Application URL
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: "8px",
                background: isDarkMode ? "rgba(234, 88, 12, 0.1)" : "rgba(234, 88, 12, 0.05)",
                border: `1px solid ${isDarkMode ? "rgba(234, 88, 12, 0.3)" : "rgba(234, 88, 12, 0.2)"}`,
              }}
            >
              <Typography
                component="a"
                href={runtime.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#F97316",
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  textDecoration: "none",
                  flex: 1,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {runtime.url}
              </Typography>
              <Tooltip title="Open in new tab" arrow>
                <IconButton
                  size="small"
                  onClick={() => window.open(runtime.url, "_blank")}
                  sx={{ color: "#F97316" }}
                >
                  <RocketLaunch fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {!runtime?.url && (
          <Box sx={{ mt: 2, p: 2, borderRadius: "8px", background: isDarkMode ? "#1e293b" : "#f1f5f9" }}>
            <Typography sx={{ fontSize: "0.85rem", color: isDarkMode ? "#94a3b8" : "#64748b" }}>
              No app runtime yet — it'll appear here once your first build succeeds.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Build Status Summary */}
      <Box
        sx={{
          p: 3,
          borderRadius: "16px",
          background: isDarkMode ? "#0f172a" : "#f8fafc",
          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: isDarkMode ? "#e2e8f0" : "#1e293b",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Build sx={{ color: "#f59e0b" }} />
          Latest Build
        </Typography>
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              Status
            </Typography>
            {(() => {
              // Fix "assume Failed on unknown state" — a fresh app with
              // buildCounter=0 was showing "Failed" when no build has
              // even been triggered yet. Only render "Failed" when the
              // build is explicitly failed; otherwise "Not started" /
              // "Pending" for the empty and unknown states.
              const s = build?.status;
              const counter = build?.buildCounter || 0;
              let label, bg, fg;
              if (s === "ready") { label = "Ready"; bg = "rgba(34,197,94,0.15)"; fg = "#22c55e"; }
              else if (s === "building") { label = "Building"; bg = "rgba(245,158,11,0.15)"; fg = "#f59e0b"; }
              else if (s === "failed") { label = "Failed"; bg = "rgba(239,68,68,0.15)"; fg = "#ef4444"; }
              else if (counter === 0) { label = "Not started"; bg = "rgba(100,116,139,0.15)"; fg = "#64748b"; }
              else { label = "Pending"; bg = "rgba(100,116,139,0.15)"; fg = "#64748b"; }
              return (
                <Chip
                  size="small"
                  label={label}
                  sx={{ background: bg, color: fg, fontWeight: 600 }}
                />
              );
            })()}
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              Build Count
            </Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
              {build?.buildCounter || 0}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", mb: 0.5 }}>
              Last Trigger
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
              {build?.latestBuildReason || "N/A"}
            </Typography>
          </Box>
        </Box>
        {build?.message && build?.status === "failed" && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: "8px", background: isDarkMode ? "#1e293b" : "#fef2f2" }}>
            <Typography sx={{ fontSize: "0.8rem", color: "#ef4444", fontFamily: "monospace" }}>
              {humanizeBuildMessage(build.message)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Runtime Logs */}
      <Box
        sx={{
          p: 3,
          borderRadius: "16px",
          background: isDarkMode ? "#0f172a" : "#f8fafc",
          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: isDarkMode ? "#e2e8f0" : "#1e293b",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Storage sx={{ color: "#22c55e" }} />
            Runtime Logs
          </Typography>
          <IconButton
            size="small"
            onClick={onRefreshLogs}
            disabled={isLoadingLogs}
            sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
          >
            <Refresh
              fontSize="small"
              sx={{
                animation: isLoadingLogs ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Box>

        {/* Log Filter Controls */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Filter logs..."
            value={logFilter}
            onChange={(e) => setLogFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem", color: isDarkMode ? "#64748b" : "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.8rem",
                background: isDarkMode ? "#1e293b" : "#fff",
                "& fieldset": {
                  borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                },
                "&:hover fieldset": {
                  borderColor: isDarkMode ? "#475569" : "#cbd5e1",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#F97316",
                },
              },
              "& .MuiInputBase-input": {
                color: isDarkMode ? "#e2e8f0" : "#1e293b",
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              startAdornment={
                <FilterIcon sx={{ fontSize: "1rem", color: isDarkMode ? "#64748b" : "#94a3b8", mr: 0.5 }} />
              }
              sx={{
                fontSize: "0.8rem",
                background: isDarkMode ? "#1e293b" : "#fff",
                color: isDarkMode ? "#e2e8f0" : "#1e293b",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode ? "#475569" : "#cbd5e1",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#F97316",
                },
              }}
            >
              <MenuItem value="ALL">All Levels</MenuItem>
              <MenuItem value="ERROR" sx={{ color: "#ef4444" }}>ERROR</MenuItem>
              <MenuItem value="WARN" sx={{ color: "#f59e0b" }}>WARN</MenuItem>
              <MenuItem value="INFO" sx={{ color: "#22c55e" }}>INFO</MenuItem>
              <MenuItem value="DEBUG" sx={{ color: "#8b5cf6" }}>DEBUG</MenuItem>
              <MenuItem value="TRACE" sx={{ color: "#64748b" }}>TRACE</MenuItem>
            </Select>
          </FormControl>
          <Chip
            size="small"
            label={`${filteredLogs.length} / ${parsedLogs.length} lines`}
            sx={{
              fontSize: "0.7rem",
              background: isDarkMode ? "#1e293b" : "#f1f5f9",
              color: isDarkMode ? "#94a3b8" : "#64748b",
            }}
          />
        </Box>

        <Box
          sx={{
            height: 300,
            borderRadius: "8px",
            background: isDarkMode ? "#0d1117" : "#1e293b",
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: "0.75rem",
            p: 2,
          }}
        >
          {isLoadingLogs ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredLogs.length > 0 ? (
            filteredLogs.map((log) => (
              <Box
                key={log.id}
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 0.5,
                  "&:hover": {
                    background: "rgba(255,255,255,0.03)",
                  },
                }}
              >
                {/* Timestamp */}
                {log.timestamp && (
                  <Tooltip
                    title={log.timestamp.toISOString()}
                    arrow
                    placement="top"
                  >
                    <Typography
                      component="span"
                      sx={{
                        color: "#64748b",
                        fontSize: "0.7rem",
                        flexShrink: 0,
                        cursor: "default",
                      }}
                    >
                      {log.timestamp.toLocaleTimeString()}
                    </Typography>
                  </Tooltip>
                )}
                {/* Log Level */}
                {log.level && (
                  <Typography
                    component="span"
                    sx={{
                      color: getLogLevelColor(log.level),
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      minWidth: 45,
                    }}
                  >
                    {log.level}
                  </Typography>
                )}
                {/* Message */}
                <Typography
                  component="span"
                  sx={{
                    color: "#e2e8f0",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {log.message || log.raw}
                </Typography>
              </Box>
            ))
          ) : parsedLogs.length > 0 ? (
            <Typography sx={{ color: "#64748b", textAlign: "center", mt: 4 }}>
              No logs match your filter criteria.
            </Typography>
          ) : (
            <Typography sx={{ color: "#64748b", textAlign: "center", mt: 4 }}>
              {runtime?.serviceName ? "No logs available — the app is currently sleeping." : "No app deployed yet."}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Build History Item
const BuildHistoryItem = ({ build, isSelected, onClick, isDarkMode }) => {
  const status = build?.status?.conditions?.[0];
  const isRunning = status?.reason === "BuildRunning" || status?.status === "Unknown";
  const isSuccess = status?.status === "True";
  const isFailed = status?.status === "False";

  return (
    <ListItem
      button
      selected={isSelected}
      onClick={onClick}
      sx={{
        borderRadius: "8px",
        mb: 0.5,
        background: isSelected
          ? isDarkMode ? "rgba(234, 88, 12, 0.15)" : "rgba(234, 88, 12, 0.1)"
          : "transparent",
        "&:hover": {
          background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        {isRunning ? (
          <CircularProgress size={20} sx={{ color: "#f59e0b" }} />
        ) : isSuccess ? (
          <CheckCircle sx={{ color: "#22c55e", fontSize: "1.25rem" }} />
        ) : isFailed ? (
          <ErrorIcon sx={{ color: "#ef4444", fontSize: "1.25rem" }} />
        ) : (
          <Schedule sx={{ color: "#64748b", fontSize: "1.25rem" }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={`Build #${build?.metadata?.labels?.["image.kpack.io/buildNumber"] || "N/A"}`}
        secondary={new Date(build?.metadata?.creationTimestamp).toLocaleString()}
        primaryTypographyProps={{
          fontSize: "0.85rem",
          fontWeight: isSelected ? 600 : 400,
          color: isDarkMode ? "#e2e8f0" : "#1e293b",
        }}
        secondaryTypographyProps={{
          fontSize: "0.7rem",
          color: isDarkMode ? "#64748b" : "#94a3b8",
        }}
      />
      <Chip
        size="small"
        label={isRunning ? "Building" : isSuccess ? "Success" : isFailed ? "Failed" : "Pending"}
        sx={{
          fontSize: "0.65rem",
          height: 20,
          background: isRunning
            ? "rgba(245, 158, 11, 0.15)"
            : isSuccess
            ? "rgba(34, 197, 94, 0.15)"
            : isFailed
            ? "rgba(239, 68, 68, 0.15)"
            : "rgba(100, 116, 139, 0.15)",
          color: isRunning ? "#f59e0b" : isSuccess ? "#22c55e" : isFailed ? "#ef4444" : "#64748b",
        }}
      />
    </ListItem>
  );
};

// Pipeline Steps Panel
const PipelineStepsPanel = ({ logs, currentStep, isDarkMode, selectedStep, onStepClick, buildCompleted }) => {
  // Count logs per step — this is the canonical signal for which
  // step the build has touched. The previous detectStep() only looked
  // at the LAST log line and bailed to currentStep=0 when that line
  // didn't contain a step keyword, leaving every chip pending forever
  // even when 100+ log lines for later steps existed.
  const stepLogCounts = useMemo(() => {
    const counts = {};
    BUILD_STEPS.forEach((s) => (counts[s.key] = 0));
    logs.forEach((log) => {
      if (log.step) {
        counts[log.step] = (counts[log.step] || 0) + 1;
      }
    });
    return counts;
  }, [logs]);

  // The highest-indexed step that has any logs is the CURRENT step;
  // every earlier step with logs is COMPLETE. When the build
  // finishes overall, every step is complete.
  const activeStep = useMemo(() => {
    if (buildCompleted) return BUILD_STEPS.length;
    for (let i = BUILD_STEPS.length - 1; i >= 0; i--) {
      if ((stepLogCounts[BUILD_STEPS[i].key] || 0) > 0) {
        return i;
      }
    }
    return 0;
  }, [stepLogCounts, buildCompleted]);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: isDarkMode ? "#e2e8f0" : "#1e293b",
          }}
        >
          Build Pipeline Steps
        </Typography>
        {selectedStep !== null && (
          <Chip
            size="small"
            label="Show All"
            onClick={() => onStepClick(null)}
            sx={{
              fontSize: "0.7rem",
              height: 22,
              background: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              cursor: "pointer",
              "&:hover": {
                background: isDarkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)",
              },
            }}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {BUILD_STEPS.map((step, idx) => {
          const isComplete = idx < activeStep;
          const isActive = idx === activeStep;
          const isSelected = selectedStep === step.key;
          const logCount = stepLogCounts[step.key] || 0;

          const handleClick = (e) => {
            e.stopPropagation();
            onStepClick && onStepClick(isSelected ? null : step.key);
          };

          return (
            <Tooltip
              key={step.key}
              title={`${step.description}${logCount > 0 ? ` (${logCount} lines)` : ''}`}
              arrow
            >
              <Box
                component={motion.div}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ delay: idx * 0.1 }}
                onClick={handleClick}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  cursor: "pointer",
                  background: isSelected
                    ? "rgba(139, 92, 246, 0.2)"
                    : isComplete
                    ? "rgba(34, 197, 94, 0.15)"
                    : isActive
                    ? "rgba(234, 88, 12, 0.15)"
                    : isDarkMode
                    ? "#1e293b"
                    : "#f1f5f9",
                  border: `2px solid ${
                    isSelected
                      ? "#8b5cf6"
                      : isComplete
                      ? "#22c55e"
                      : isActive
                      ? "#F97316"
                      : isDarkMode
                      ? "#334155"
                      : "#e2e8f0"
                  }`,
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: isSelected ? "#a78bfa" : "#8b5cf6",
                  },
                }}
              >
                {isActive && !isSelected && (
                  <Box
                    component={motion.div}
                    animate={{ opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(234, 88, 12, 0.1)",
                    }}
                  />
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isComplete ? (
                    <CheckCircle sx={{ fontSize: "0.9rem", color: isSelected ? "#8b5cf6" : "#22c55e" }} />
                  ) : isActive ? (
                    <CircularProgress size={14} sx={{ color: isSelected ? "#8b5cf6" : "#F97316" }} />
                  ) : (
                    <Schedule
                      sx={{
                        fontSize: "0.9rem",
                        color: isSelected ? "#8b5cf6" : isDarkMode ? "#64748b" : "#94a3b8",
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected
                        ? "#8b5cf6"
                        : isComplete
                        ? "#22c55e"
                        : isActive
                        ? "#F97316"
                        : isDarkMode
                        ? "#94a3b8"
                        : "#64748b",
                    }}
                  >
                    {step.label}
                  </Typography>
                  {logCount > 0 && (
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        color: isSelected ? "#8b5cf6" : isDarkMode ? "#64748b" : "#94a3b8",
                        ml: 0.5,
                      }}
                    >
                      ({logCount})
                    </Typography>
                  )}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

// Domains Panel - manage custom domains for an app
const DomainsPanel = ({ appName, projectId, showError }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [assignments, setAssignments] = useState([]);
  const [availableDomains, setAvailableDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ subdomain: "", domain: "", apex: false });
  const [isAdding, setIsAdding] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchAssignments = useCallback(() => {
    if (!projectId) return;
    setIsLoading(true);
    getDomainAssignments(projectId, (result, err) => {
      setIsLoading(false);
      if (result) {
        setAssignments(result.filter((a) => a.appName === appName));
      }
    }, appName);
  }, [projectId, appName]);

  const fetchDomains = useCallback(() => {
    if (!projectId) return;
    getDomains(projectId, (result) => {
      if (result) setAvailableDomains(result);
    });
  }, [projectId]);

  useEffect(() => {
    fetchAssignments();
    fetchDomains();
  }, [fetchAssignments, fetchDomains]);

  // DELEGATION and SUBDOMAIN_DELEGATION both mean "no manual DNS — fully automatic".
  const isDelegated = (method) => method === "DELEGATION" || method === "SUBDOMAIN_DELEGATION";

  // Auto-refresh pending assignments (5s for delegated zones, 10s for CNAME_ONLY)
  useEffect(() => {
    const hasPending = assignments.some((a) => a.status !== "ACTIVE" && a.status !== "FAILED");
    if (!hasPending) return;
    const hasDelegation = assignments.some((a) => {
      const d = availableDomains.find((dom) => dom.domain === a.domainName);
      return isDelegated(d?.dnsMethod);
    });
    const interval = setInterval(fetchAssignments, hasDelegation ? 5000 : 10000);
    return () => clearInterval(interval);
  }, [assignments, availableDomains, fetchAssignments]);

  const handleAdd = () => {
    if ((!addForm.apex && !addForm.subdomain) || !addForm.domain) return;
    setIsAdding(true);
    assignDomainApi(
      { subdomain: addForm.apex ? "" : addForm.subdomain, domain: addForm.domain, appName, projectId },
      (result, err) => {
        setIsAdding(false);
        if (result) {
          setShowAddDialog(false);
          setAddForm({ subdomain: "", domain: "", apex: false });
          fetchAssignments();
        } else {
          showError?.(err || "Failed to add domain");
        }
      }
    );
  };

  const handleRemove = (assignmentId, fqdn) => {
    if (!window.confirm(`Remove domain ${fqdn}?`)) return;
    unassignDomainApi(assignmentId, (result, err) => {
      if (!err) {
        setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
      } else {
        showError?.(err || "Failed to remove domain");
      }
    });
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getDnsMethod = (domainName) => {
    const d = availableDomains.find((dom) => dom.domain === domainName);
    return d?.dnsMethod || "CNAME_ONLY";
  };

  const statusChip = (status, domainName) => {
    const isDelegation = isDelegated(getDnsMethod(domainName));
    const map = {
      ACTIVE: { color: "success", label: "Live" },
      DNS_VERIFIED: { color: "info", label: "Issuing cert..." },
      PENDING: { color: "warning", label: isDelegation ? "Issuing cert..." : "Waiting for DNS" },
      FAILED: { color: "error", label: "Failed" },
    };
    const s = map[status] || { color: "default", label: status || "Unknown" };
    return <Chip label={s.label} color={s.color} size="small" />;
  };

  const cardSx = {
    p: 2, mb: 1.5, borderRadius: "12px",
    background: isDarkMode ? "rgba(30,41,59,0.7)" : "rgba(248,250,252,0.9)",
    border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: isDarkMode ? "#e2e8f0" : "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
          <DomainIcon sx={{ fontSize: 20 }} /> Custom Domains
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)}
          sx={{ textTransform: "none", borderColor: "#F97316", color: "#F97316" }}>
          Add Domain
        </Button>
      </Box>

      {/* Domain list */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
      ) : assignments.length === 0 ? (
        <Box sx={{ ...cardSx, textAlign: "center", py: 4 }}>
          <Typography sx={{ color: isDarkMode ? "#94a3b8" : "#64748b", mb: 1 }}>No custom domains assigned</Typography>
          <Typography sx={{ color: isDarkMode ? "#64748b" : "#94a3b8", fontSize: "0.85rem" }}>
            Click "Add Domain" to assign a custom domain to this app.
          </Typography>
        </Box>
      ) : (
        assignments.map((a) => (
          <Box key={a.id} sx={cardSx}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
                  {a.status === "ACTIVE" ? (
                    <a href={`https://${a.fqdn}`} target="_blank" rel="noopener noreferrer"
                      style={{ color: "#F97316", textDecoration: "none" }}>
                      {a.fqdn}
                    </a>
                  ) : a.fqdn}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: isDarkMode ? "#64748b" : "#94a3b8", mt: 0.5 }}>
                  {a.subdomain === "@" ? a.domainName + " (apex)" : `${a.subdomain}.${a.domainName}`}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {statusChip(a.status, a.domainName)}
                <IconButton size="small" onClick={() => handleRemove(a.id, a.fqdn)}
                  sx={{ color: isDarkMode ? "#64748b" : "#94a3b8" }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Status info for pending/verifying domains */}
            {(a.status === "PENDING" || a.status === "DNS_VERIFIED") && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: "8px", background: isDarkMode ? "rgba(234, 88, 12,0.1)" : "rgba(234, 88, 12,0.05)", border: `1px solid ${isDarkMode ? "rgba(234, 88, 12,0.3)" : "rgba(234, 88, 12,0.2)"}` }}>
                {isDelegated(getDnsMethod(a.domainName)) ? (
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#F97316" }}>
                    {a.status === "DNS_VERIFIED" ? "DNS verified — Issuing certificate..." : "Setting up automatically — certificate will be ready in ~90s..."}
                  </Typography>
                ) : (
                  <>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#F97316", mb: 1 }}>
                      {a.status === "DNS_VERIFIED" ? "DNS detected — Issuing certificate..." : "Add these 2 CNAME records at your DNS provider:"}
                    </Typography>
                    {a.status === "PENDING" && (
                      <>
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b" }}>1. Route traffic:</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                            <Typography sx={{ fontSize: "0.8rem", fontFamily: "monospace", color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
                              {a.fqdn} → ingress.apps.clue2.app
                            </Typography>
                            <IconButton size="small" onClick={() => copyToClipboard(`${a.fqdn} CNAME ingress.apps.clue2.app`, `t-${a.id}`)}>
                              {copied === `t-${a.id}` ? <CheckCircle sx={{ fontSize: 14, color: "#22c55e" }} /> : <CopyIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </Box>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b" }}>2. TLS certificate:</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                            <Typography sx={{ fontSize: "0.8rem", fontFamily: "monospace", color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
                              _acme-challenge.{a.fqdn} → _acme-challenge.{a.fqdn}.apps.clue2.app
                            </Typography>
                            <IconButton size="small" onClick={() => copyToClipboard(`_acme-challenge.${a.fqdn} CNAME _acme-challenge.${a.fqdn}.apps.clue2.app`, `c-${a.id}`)}>
                              {copied === `c-${a.id}` ? <CheckCircle sx={{ fontSize: 14, color: "#22c55e" }} /> : <CopyIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </Box>
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        ))
      )}

      {/* Add Domain Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Custom Domain</DialogTitle>
        <DialogContent>
          <FormControl size="small" sx={{ mt: 1, width: "100%" }}>
            <Select
              value={addForm.domain}
              onChange={(e) => {
                const dom = availableDomains.find((d) => d.domain === e.target.value);
                const canApex = dom && !dom.isPlatformDomain && (dom.dnsMethod === "DELEGATION" || dom.dnsMethod === "SUBDOMAIN_DELEGATION");
                setAddForm((f) => ({ ...f, domain: e.target.value, apex: canApex ? f.apex : false }));
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>Select domain</MenuItem>
              {availableDomains.map((d) => (
                <MenuItem key={d.id} value={d.domain}>
                  {d.domain} {d.isPlatformDomain ? "(platform)" : isDelegated(d.dnsMethod) ? "(auto)" : "(CNAME)"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(() => {
            const selectedDomain = addForm.domain ? availableDomains.find((d) => d.domain === addForm.domain) : null;
            const canApex = selectedDomain && !selectedDomain.isPlatformDomain && (selectedDomain.dnsMethod === "DELEGATION" || selectedDomain.dnsMethod === "SUBDOMAIN_DELEGATION");
            return canApex ? (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={addForm.apex}
                    onChange={(e) => setAddForm((f) => ({ ...f, apex: e.target.checked, subdomain: e.target.checked ? "" : f.subdomain }))}
                    size="small"
                  />
                }
                label={<Typography sx={{ fontSize: "0.85rem" }}>Map domain directly (no subdomain)</Typography>}
              />
            ) : null;
          })()}
          {!addForm.apex && (
            <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                label="Subdomain"
                placeholder="api"
                value={addForm.subdomain}
                onChange={(e) => setAddForm((f) => ({ ...f, subdomain: e.target.value }))}
                size="small"
                sx={{ flex: 1 }}
              />
              {addForm.domain && (
                <Typography sx={{ color: "#64748b" }}>.{addForm.domain}</Typography>
              )}
            </Box>
          )}
          {((addForm.apex && addForm.domain) || (addForm.subdomain && addForm.domain)) && (() => {
            const selectedDomain = availableDomains.find((d) => d.domain === addForm.domain);
            const isDelegation = isDelegated(selectedDomain?.dnsMethod);
            const isPlatform = selectedDomain?.isPlatformDomain;
            const displayFqdn = addForm.apex ? addForm.domain : `${addForm.subdomain}.${addForm.domain}`;
            return (
              <Alert severity={isDelegation || isPlatform ? "success" : "info"} sx={{ mt: 2 }}>
                This will create <strong>{displayFqdn}</strong> pointing to this app.
                {addForm.apex ? (
                  <> Maps the apex domain directly. Certificate will be issued automatically.</>
                ) : isDelegation || isPlatform ? (
                  <> Certificate will be issued automatically.</>
                ) : (
                  <> You&apos;ll need to add 2 CNAME records at your DNS provider.</>
                )}
              </Alert>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained"
            disabled={(!addForm.apex && !addForm.subdomain) || !addForm.domain || isAdding}>
            {isAdding ? <CircularProgress size={20} /> : "Add Domain"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Main Modal Component
// Showcase Panel — project admins submit the app for inclusion in the
// public showcase (console.clue2.app/showcase). A SYSTEM admin reviews
// the pending queue and approves or denies; this panel surfaces the
// current state, denial reasons, and re-submit controls.
const ShowcasePanel = ({ imageUid, appName, showError }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState({
    displayName: "",
    showcaseUrl: "",
    description: "",
    imageUrl: "",
    category: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Use API_URL from constants — getEnv resolves at runtime via the
  // env-config.js injected into index.html. Reading process.env directly
  // bakes in the build-time value, which is undefined when env-config
  // hadn't been injected at build time → fetch URL becomes
  // `undefined/showcase/...` → browser treats as relative → SPA serves
  // index.html (HTML) → JSON.parse explodes with "<!doctype" error.
  const apiBase = API_URL;
  const authHeader = () => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const refresh = useCallback(() => {
    if (!imageUid) return;
    fetch(`${apiBase}/showcase/app/${imageUid}`, { headers: authHeader() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((s) => {
        setState(s);
        setForm({
          displayName: s.displayName || "",
          showcaseUrl: s.showcaseUrl || "",
          description: s.description || "",
          imageUrl: s.imageUrl || "",
          category: s.category || "",
        });
        setLoadError(null);
      })
      .catch((err) => setLoadError(err.message || "Failed to load showcase state"));
  }, [apiBase, imageUid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = () => {
    if (!form.showcaseUrl.trim()) {
      showError && showError("Showcase URL is required");
      return;
    }
    setSubmitting(true);
    fetch(`${apiBase}/showcase/app/${imageUid}/submit`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(form),
    })
      .then((r) => (r.ok ? r.json() : r.text().then((t) => Promise.reject(new Error(t || `HTTP ${r.status}`)))))
      .then(() => refresh())
      .catch((err) => showError && showError(err.message || "Submit failed"))
      .finally(() => setSubmitting(false));
  };

  const withdraw = () => {
    setSubmitting(true);
    fetch(`${apiBase}/showcase/app/${imageUid}/withdraw`, {
      method: "POST",
      headers: authHeader(),
    })
      .then((r) => (r.ok ? r.json() : r.text().then((t) => Promise.reject(new Error(t || `HTTP ${r.status}`)))))
      .then(() => refresh())
      .catch((err) => showError && showError(err.message || "Withdraw failed"))
      .finally(() => setSubmitting(false));
  };

  if (!imageUid) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Showcase submission is available once the first build of <strong>{appName}</strong> completes.
        </Typography>
      </Box>
    );
  }

  const status = state?.status;
  const banner = (() => {
    switch (status) {
      case "PENDING":
        return { color: "#92400e", bg: "#fffbeb", text: "Submitted — awaiting Clue2App review." };
      case "APPROVED":
        return { color: "#065f46", bg: "#d1fae5", text: "Live on the public showcase." };
      case "DENIED":
        return {
          color: "#991b1b",
          bg: "#fee2e2",
          text: `Denied: ${state?.decisionReason || "no reason provided"}. Edit below and re-submit.`,
        };
      default:
        return null;
    }
  })();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        Show in Public Showcase
      </Typography>
      <Typography variant="body2" sx={{ color: isDarkMode ? "#94a3b8" : "#475569", mb: 3 }}>
        Submit this app for inclusion in the public showcase at
        console.clue2.app/showcase. A Clue2App admin reviews each
        submission before it goes live.
      </Typography>

      {loadError && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: "#fee2e2", borderRadius: 1 }}>
          <Typography color="error">{loadError}</Typography>
        </Box>
      )}

      {banner && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: banner.bg,
            color: banner.color,
            borderRadius: 1,
            fontWeight: 500,
          }}
        >
          {banner.text}
        </Box>
      )}

      <Stack spacing={2} sx={{ maxWidth: 640 }}>
        <TextField
          label="Display name"
          placeholder={appName ? `e.g. ${appName.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}` : "Display name"}
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          fullWidth
          helperText={`Shown on the public showcase tile. Leave blank to use the app's internal name (${appName || "—"}).`}
        />
        <TextField
          label="Public URL"
          placeholder="https://your-app.example.com"
          value={form.showcaseUrl}
          onChange={(e) => setForm({ ...form, showcaseUrl: e.target.value })}
          fullWidth
          required
          helperText="Where visitors land when they click your tile."
        />
        <TextField
          label="Short description"
          placeholder="One sentence about what your app does."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          fullWidth
          multiline
          maxRows={3}
        />
        <Box>
          <TextField
            label="Tile image URL"
            placeholder="https://… (optional, ~1200x630)"
            value={form.imageUrl}
            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
            fullWidth
            helperText="Paste a URL, OR upload an image below — uploads override the URL."
          />
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={submitting}
            >
              Upload screenshot
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setSubmitting(true);
                  const fd = new FormData();
                  fd.append("file", file);
                  fetch(`${apiBase}/showcase/app/${imageUid}/screenshot`, {
                    method: "POST",
                    headers: authHeader(),
                    body: fd,
                  })
                    .then((r) =>
                      r.ok
                        ? r.json()
                        : r.text().then((t) => Promise.reject(new Error(t || `HTTP ${r.status}`)))
                    )
                    .then(() => refresh())
                    .catch((err) => showError && showError(err.message || "Upload failed"))
                    .finally(() => {
                      setSubmitting(false);
                      e.target.value = ""; // allow re-upload of the same file
                    });
                }}
              />
            </Button>
            {form.imageUrl && (
              <Box
                component="img"
                src={form.imageUrl}
                alt=""
                sx={{ height: 40, borderRadius: 0.5, opacity: 0.85 }}
              />
            )}
          </Box>
        </Box>
        <TextField
          label="Category"
          placeholder="e.g. Productivity, Internal, Demo"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          fullWidth
        />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ShowcaseIcon />}
          disabled={submitting || !form.showcaseUrl.trim()}
          onClick={submit}
        >
          {status === "PENDING" || status === "APPROVED" ? "Re-submit" : "Submit for review"}
        </Button>
        {status && (
          <Button variant="outlined" color="warning" disabled={submitting} onClick={withdraw}>
            Withdraw
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default function AppFlowModal({ open, onClose, appData, onRefresh, showError, showSuccess, currentUser, onRename, onDelete, initialTab = 0 }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { currentGroup } = useSharedProps();
  const [currentStage, setCurrentStage] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [builds, setBuilds] = useState([]);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [buildLogs, setBuildLogs] = useState([]);
  const [runtimeLogs, setRuntimeLogs] = useState([]);
  const [isLoadingBuilds, setIsLoadingBuilds] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isLoadingRuntimeLogs, setIsLoadingRuntimeLogs] = useState(false);
  const [showBuildsList, setShowBuildsList] = useState(true);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);

  // Extract build and runtime data from appData (supports both old and new formats).
  // Memoized so downstream useMemo deps (pipelineState) don't re-fire every render.
  const buildData = useMemo(
    () => appData?._raw?.build || appData,
    [appData]
  );
  const runtimeData = useMemo(
    () => appData?.runtime || appData?._raw?.runtime || {},
    [appData]
  );
  const buildInfo = useMemo(() => appData?.build || {}, [appData]);

  // Filter logs by selected step
  const filteredLogs = useMemo(() => {
    if (!selectedStep) return buildLogs;
    return buildLogs.filter(log => log.step === selectedStep);
  }, [buildLogs, selectedStep]);

  const stages = [
    { icon: GitHub, title: "Git Repo", subtitle: "Source Code" },
    { icon: Build, title: "App Build", subtitle: "App image" },
    { icon: Storage, title: "Image Storage", subtitle: "Built image saved" },
    { icon: Cloud, title: "Live App", subtitle: "Auto-scaling" },
  ];

  // Fetch builds for the application
  const fetchBuilds = useCallback(() => {
    const imageName = buildData?.metadata?.name;
    if (!imageName) return;

    setIsLoadingBuilds(true);
    getBuilds({ imageName }, (result, err) => {
      setIsLoadingBuilds(false);
      if (!err && result) {
        const sortedBuilds = [...result].sort(
          (a, b) =>
            new Date(b.metadata.creationTimestamp) -
            new Date(a.metadata.creationTimestamp)
        );
        setBuilds(sortedBuilds);
        // Auto-select first build only if none is selected yet
        setSelectedBuild((prev) => prev || (sortedBuilds.length > 0 ? sortedBuilds[0] : null));
      }
    });
  }, [buildData?.metadata?.name]);

  // Fetch runtime logs
  const fetchRuntimeLogs = useCallback((showLoading = false) => {
    const serviceName = runtimeData?.serviceName || appData?.name?.replace(/-image$/, "");
    if (!serviceName) return;

    // Skip fetching logs if the service doesn't exist yet (e.g., first build)
    // to avoid 401 errors that trigger a global logout
    const runtimeStatus = runtimeData?.status;
    if (!runtimeStatus || runtimeStatus === "stopped") return;

    if (showLoading) {
      setIsLoadingRuntimeLogs(true);
    }

    getServiceInstanceLogs({ name: serviceName }, (result, err) => {
      setIsLoadingRuntimeLogs(false);
      if (!err && result) {
        const logsText = typeof result === "string" ? result : "";
        const lines = logsText.split("\n").filter(Boolean).slice(-200); // Last 200 lines
        setRuntimeLogs(lines);
      }
    });
  }, [runtimeData?.serviceName, runtimeData?.status, appData?.name]);

  // Fetch logs for selected build
  const fetchBuildLogs = useCallback((showLoading = false) => {
    const podName = selectedBuild?.status?.podName;

    if (!podName) {
      // Pod might have been cleaned up - show a message
      const buildNumber = selectedBuild?.metadata?.labels?.["image.kpack.io/buildNumber"];
      setBuildLogs([{
        line: `Build pod has been cleaned up. Logs for build #${buildNumber || 'N/A'} are no longer available.`,
        step: null,
      }, {
        line: "Tip: View build logs while the build is in progress, or check the platform's log aggregation system.",
        step: null,
      }]);
      return;
    }

    if (showLoading) {
      setIsLoadingLogs(true);
    }

    getBuildLogs(
      { podName },
      (result, err) => {
        if (showLoading) {
          setIsLoadingLogs(false);
        }
        if (!err && result) {
          // Parse logs into array - result is the log text
          const logsText = typeof result === "string" ? result : "";
          const lines = logsText.split("\n").filter(Boolean);

          // Track current step and assign to all lines within that section
          let currentStep = null;
          const logLines = lines.map((line) => {
            const detectedStep = detectStepFromLine(line);
            if (detectedStep) {
              currentStep = detectedStep;
            }
            return {
              line,
              timestamp: new Date().toISOString(),
              step: currentStep,
            };
          });
          setBuildLogs(logLines);
        }
        // Don't clear logs on error during polling - keep existing logs
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuild?.status?.podName]);

  // Detect build step header from log line
  // Kpack/buildpack logs use patterns like "===== prepare =====" or "===> DETECTING"
  const detectStepFromLine = (line) => {
    const upperLine = line.toUpperCase();

    // Common buildpack phase markers
    const stepPatterns = [
      { patterns: ["PREPARE", "SETUP", "COMPLETION"], key: "PREPARE" },
      { patterns: ["DETECT"], key: "DETECT" },
      { patterns: ["ANALYZE"], key: "ANALYZE" },
      { patterns: ["RESTORE", "CACHE"], key: "RESTORE" },
      { patterns: ["BUILD", "COMPILE"], key: "BUILD" },
      { patterns: ["EXPORT", "PUSH"], key: "EXPORT" },
    ];

    // Detect on lines that look like phase headers
    const isPhaseHeader =
      upperLine.includes("=====") ||  // ===== prepare =====
      upperLine.includes("===>") ||   // ===> DETECTING
      upperLine.includes("***") ||
      upperLine.includes("---");

    if (isPhaseHeader) {
      for (const { patterns, key } of stepPatterns) {
        for (const pattern of patterns) {
          if (upperLine.includes(pattern)) {
            return key;
          }
        }
      }
    }
    return null;
  };

  // Keep stable refs for fetch functions to avoid triggering the open effect
  const fetchBuildsRef = React.useRef(fetchBuilds);
  fetchBuildsRef.current = fetchBuilds;
  const fetchRuntimeLogsRef = React.useRef(fetchRuntimeLogs);
  fetchRuntimeLogsRef.current = fetchRuntimeLogs;

  // Fetch builds and runtime logs when modal opens
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (open && appData) {
      fetchBuildsRef.current();
      fetchRuntimeLogsRef.current(true);
      // Only reset tab when modal first opens, not on every re-render
      if (!prevOpenRef.current) {
        setActiveTab(initialTab);
      }
    } else {
      setBuilds([]);
      setSelectedBuild(null);
      setBuildLogs([]);
      setRuntimeLogs([]);
      setActiveTab(0);
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appData, initialTab]);

  // Fetch logs when build is selected - clear previous logs only on build change
  useEffect(() => {
    if (selectedBuild) {
      setBuildLogs([]); // Clear logs first to reset terminal
      setSelectedStep(null); // Reset step filter when changing builds
      fetchBuildLogs(true); // Show loading on initial fetch
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuild?.metadata?.uid]); // Only trigger on build change, not on every render

  // Auto-refresh logs when on Build Logs tab with a running build
  useEffect(() => {
    if (!open || activeTab !== 1) return;

    const isSelectedBuildRunning =
      selectedBuild?.status?.conditions?.[0]?.reason === "BuildRunning" ||
      selectedBuild?.status?.conditions?.[0]?.status === "Unknown";

    if (isSelectedBuildRunning && selectedBuild) {
      // Faster polling (2s) for real-time log updates
      const logsInterval = setInterval(() => {
        fetchBuildLogs();
      }, 2000);

      // Slower polling (5s) for builds list updates
      const buildsInterval = setInterval(() => {
        fetchBuilds();
      }, 5000);

      return () => {
        clearInterval(logsInterval);
        clearInterval(buildsInterval);
      };
    }
  }, [open, activeTab, selectedBuild, fetchBuilds, fetchBuildLogs]);

  // ---------------------------------------------------------------------
  // Real-state pipeline derivation (fixes the "all green when
  // buildCount=0" bug — the modal used to fake-progress through the 4
  // stages with a setInterval, ignoring what kpack + ksvc actually said.
  //
  // Sources:
  //   buildData    kpack Image resource (has spec.source.git.url,
  //                status.buildCounter, status.latestImage)
  //   builds       kpack Build resources for this Image
  //   runtimeData  knative ksvc summary (status: "running" | "stopped" | …)
  //
  // Rules:
  //   Stage 0 (Git Repo)     complete iff git URL is attached
  //   Stage 1 (App Build)    complete iff there is a Succeeded build
  //                          active   iff there is an in-progress build
  //                          failed   iff latest build's Succeeded=False
  //   Stage 2 (Image Storage) complete iff latestImage exists
  //   Stage 3 (Live App)     complete iff ksvc reports running / ready
  //                          active   iff image exists but not yet Ready
  // ---------------------------------------------------------------------
  const [isReplaying, setIsReplaying] = useState(false);

  const pipelineState = useMemo(() => {
    const gitUrl = buildData?.spec?.source?.git?.url || appData?.build?.gitUrl;
    const hasGit = !!gitUrl;

    const buildCounter =
      buildData?.status?.buildCounter || appData?.build?.buildCounter || 0;

    const latestBuild = Array.isArray(builds) && builds.length > 0 ? builds[0] : null;
    const succeededCond = latestBuild?.status?.conditions?.find(
      (c) => c.type === "Succeeded"
    );
    const buildInProgress = succeededCond?.status === "Unknown";
    const buildSucceeded = succeededCond?.status === "True";
    const buildFailed = succeededCond?.status === "False";

    const hasImage =
      !!(buildData?.status?.latestImage || appData?.build?.latestImage);

    const runtimeStatus = runtimeData?.status;
    const ksvcReady =
      runtimeStatus === "running" ||
      runtimeStatus === "ready" ||
      runtimeStatus === "Ready";

    // Per-stage status. Higher-numbered stages can be "idle" while lower
    // stages are "complete" — that's the mid-deploy state we render.
    const statuses = ["idle", "idle", "idle", "idle"];

    if (hasGit) statuses[0] = "complete";

    if (buildFailed && !buildSucceeded) statuses[1] = "failed";
    else if (buildSucceeded || hasImage) statuses[1] = "complete";
    else if (buildInProgress || buildCounter > 0) statuses[1] = "active";

    if (hasImage) statuses[2] = "complete";
    else if (buildSucceeded) statuses[2] = "active";

    if (ksvcReady) statuses[3] = "complete";
    else if (hasImage) statuses[3] = "active";

    // Per-stage duration (ms) computed from real timestamps. null when
    // we don't have both endpoints — we do NOT fabricate.
    const durations = [null, null, null, null];

    // Stage 1 (App Build) — from latest build's stack.buildStartTime /
    // buildEndTime, or from creationTimestamp → succeeded transition.
    if (latestBuild) {
      const startIso =
        latestBuild.status?.stack?.buildStartTime ||
        latestBuild.metadata?.creationTimestamp;
      const endIso =
        latestBuild.status?.stack?.buildEndTime ||
        succeededCond?.lastTransitionTime;
      if (startIso && endIso) {
        const ms = Date.parse(endIso) - Date.parse(startIso);
        if (Number.isFinite(ms) && ms > 0) durations[1] = ms;
      }
    }

    // Stage 3 (Live App) — from latestImage push timestamp (if available
    // on kpack Build) to ksvc revision Ready. We rarely have both,
    // so this often stays null. When runtimeData exposes a
    // firstReadyAt, we can wire it here.

    const highestComplete = statuses.reduce(
      (acc, s, i) => (s === "complete" ? i : acc),
      -1
    );
    const fullyDeployed = statuses.every((s) => s === "complete");

    return {
      statuses,
      durations,
      highestComplete,
      activeIndex: statuses.findIndex((s) => s === "active"),
      failedIndex: statuses.findIndex((s) => s === "failed"),
      fullyDeployed,
    };
  }, [buildData, builds, runtimeData, appData]);

  // Sync currentStage to real state whenever it changes, unless the
  // user is actively replaying the animation (in which case the replay
  // effect owns currentStage until it finishes).
  useEffect(() => {
    if (!open) {
      setCurrentStage(0);
      setIsRunning(false);
      setIsReplaying(false);
      return;
    }
    if (isReplaying) return;

    // Map real state to a currentStage integer for the description
    // block below the stages. Prefer active over complete when both
    // exist (so the description reads "Building…" instead of jumping
    // straight to "Deployment Complete").
    const nextStage =
      pipelineState.activeIndex >= 0
        ? pipelineState.activeIndex
        : pipelineState.fullyDeployed
        ? 4
        : Math.max(0, pipelineState.highestComplete + 1);

    setCurrentStage(nextStage);
    setIsRunning(pipelineState.activeIndex >= 0);
  }, [open, isReplaying, pipelineState]);

  const getStageStatus = (index) => {
    if (isReplaying) {
      // During replay, use the classic left-to-right animation shape.
      if (index < currentStage) return "complete";
      if (index === currentStage && isRunning) return "active";
      return "idle";
    }
    return pipelineState.statuses[index] || "idle";
  };

  // Replay only fires when the app is actually deployed end-to-end.
  // Otherwise the button is disabled — a "replay" of a half-built
  // deployment is meaningless and would revert to fake theater.
  const restartAnimation = () => {
    if (!pipelineState.fullyDeployed) return;
    setIsReplaying(true);
    setCurrentStage(0);
    setIsRunning(true);
    setTimeout(() => {
      const interval = setInterval(() => {
        setCurrentStage((prev) => {
          if (prev >= stages.length) {
            clearInterval(interval);
            setIsRunning(false);
            // Hand control back to the real-state binding after a beat.
            setTimeout(() => setIsReplaying(false), 800);
            return prev;
          }
          return prev + 1;
        });
      }, 1500);
    }, 500);
  };

  // Human-friendly duration formatter: 2m 14s, 12s, 890ms.
  const formatDurationMs = (ms) => {
    if (!Number.isFinite(ms) || ms < 0) return null;
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const secs = Math.round(ms / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return rem === 0 ? `${mins}m` : `${mins}m ${rem}s`;
  };

  // Trigger rebuild
  const handleRebuild = useCallback(() => {
    const imageName = buildData?.metadata?.name;
    if (!imageName || isRebuilding) return;

    setIsRebuilding(true);
    forceRebuild({ name: imageName }, (result, err) => {
      setIsRebuilding(false);
      if (err) {
        showError && showError(err);
      } else {
        // Switch to build logs tab and refresh
        setActiveTab(1);
        setTimeout(() => {
          fetchBuilds();
        }, 1000);
        onRefresh && onRefresh();
      }
    });
  }, [buildData?.metadata?.name, isRebuilding, showError, fetchBuilds, onRefresh]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(15, 23, 42, 0.8)",
          backdropFilter: "blur(8px)",
        },
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          pointerEvents: "none",
        }}
      >
        <AnimatePresence>
          {open && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              sx={{
                width: { xs: "95vw", sm: "90vw", md: "850px" },
                maxWidth: "95vw",
                maxHeight: "90dvh",
                background: isDarkMode
                  ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                  : "#ffffff",
                borderRadius: "20px",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                overflow: "hidden",
                pointerEvents: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #F97316 0%, #C2410C 100%)",
                  px: 3,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                    }}
                  >
                    {(appData?.displayName || buildData?.metadata?.displayName || "Application").replace(/-image$/, "")} - CI/CD Pipeline
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.8rem",
                    }}
                  >
                    Git to Production Flow
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Bind a Service" arrow>
                    <IconButton
                      onClick={() => setActiveTab(3)}
                      sx={{
                        color: "#fff",
                        background: "rgba(59, 130, 246, 0.2)",
                        "&:hover": { background: "rgba(59, 130, 246, 0.35)" },
                      }}
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Trigger Rebuild" arrow>
                    <IconButton
                      onClick={handleRebuild}
                      disabled={isRebuilding}
                      sx={{
                        color: "#fff",
                        background: isRebuilding
                          ? "rgba(245, 158, 11, 0.3)"
                          : "rgba(34, 197, 94, 0.2)",
                        "&:hover": { background: "rgba(34, 197, 94, 0.35)" },
                        "&.Mui-disabled": {
                          color: "rgba(255,255,255,0.5)",
                        },
                      }}
                    >
                      {isRebuilding ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                      ) : (
                        <RocketLaunch />
                      )}
                    </IconButton>
                  </Tooltip>
                  {onRename && (
                    <Tooltip title="Rename App" arrow>
                      <IconButton
                        onClick={() => {
                          onClose();
                          onRename(appData);
                        }}
                        sx={{
                          color: "#fff",
                          background: "rgba(139, 92, 246, 0.2)",
                          "&:hover": { background: "rgba(139, 92, 246, 0.35)" },
                        }}
                      >
                        <RenameIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip title="Delete App" arrow>
                      <IconButton
                        onClick={() => {
                          onClose();
                          onDelete(appData);
                        }}
                        sx={{
                          color: "#fff",
                          background: "rgba(239, 68, 68, 0.2)",
                          "&:hover": { background: "rgba(239, 68, 68, 0.35)" },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={
                      pipelineState.fullyDeployed
                        ? "Replay pipeline animation"
                        : "Replay available once the deployment completes"
                    }
                    arrow
                  >
                    {/* span wrapper: IconButton doesn't forward tooltips when disabled */}
                    <span>
                      <IconButton
                        onClick={restartAnimation}
                        disabled={!pipelineState.fullyDeployed || isReplaying}
                        sx={{
                          color: "#fff",
                          background: "rgba(255,255,255,0.15)",
                          "&:hover": { background: "rgba(255,255,255,0.25)" },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.4)",
                            background: "rgba(255,255,255,0.05)",
                          },
                        }}
                      >
                        <PlayArrow />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    onClick={onClose}
                    sx={{
                      color: "#fff",
                      background: "rgba(255,255,255,0.15)",
                      "&:hover": { background: "rgba(255,255,255,0.25)" },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
              </Box>

              {/* Tabs */}
              <Box
                sx={{
                  borderBottom: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                  px: 2,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(e, v) => setActiveTab(v)}
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      textTransform: "none",
                      minHeight: 44,
                      color: isDarkMode ? "#94a3b8" : "#64748b",
                      "&.Mui-selected": {
                        color: "#F97316",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#F97316",
                    },
                  }}
                >
                  <Tab label="Pipeline" />
                  <Tab
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        Build Logs
                        {builds.some(
                          (b) =>
                            b?.status?.conditions?.[0]?.reason === "BuildRunning"
                        ) && (
                          <Box
                            component={motion.div}
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#f59e0b",
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                  <Tab label="Runtime" />
                  <Tab
                    label="Bindings"
                    icon={<LinkIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                  />
                  <Tab label="Metrics" />
                  <Tab label="Domains" />
                  <Tab label="Showcase" icon={<ShowcaseIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                </Tabs>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3, overflowY: "auto", overflowX: "hidden", overscrollBehavior: "contain", maxHeight: "calc(90dvh - 140px)" }}>
                {/* Tab 0: Pipeline Visualization */}
                {activeTab === 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: { xs: 1, md: 2 },
                        mb: 3,
                        overflow: "hidden",
                      }}
                    >
                      {stages.map((stage, index) => (
                        <Box
                          key={stage.title}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <StageCard
                            icon={stage.icon}
                            title={stage.title}
                            subtitle={stage.subtitle}
                            status={getStageStatus(index)}
                            delay={index * 0.1}
                            isActive={index === currentStage}
                            duration={
                              !isReplaying
                                ? formatDurationMs(pipelineState.durations[index])
                                : null
                            }
                          />
                          {index < stages.length - 1 && (
                            <ConnectionLine
                              active={
                                isReplaying
                                  ? currentStage > index
                                  : pipelineState.statuses[index] === "complete"
                              }
                              delay={index * 0.3}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>

                    {/* Stage Description */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentStage}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            background: isDarkMode ? "#0f172a" : "#f8fafc",
                            border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                            mb: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "#F97316",
                              fontSize: "0.9rem",
                              mb: 1,
                            }}
                          >
                            {currentStage === 0 && "Watching Git Repository"}
                            {currentStage === 1 && "Building your app"}
                            {currentStage === 2 && "Saving built image"}
                            {currentStage === 3 && "Going live"}
                            {currentStage >= 4 && "Deployment Complete!"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              color: isDarkMode ? "#94a3b8" : "#64748b",
                            }}
                          >
                            {currentStage === 0 &&
                              "The platform watches your Git repo for changes. When you push a commit, it automatically triggers a new build."}
                            {currentStage === 1 &&
                              "The platform auto-detects your app type and builds an optimized image — no Dockerfile required."}
                            {currentStage === 2 &&
                              "Your built app image is saved to the platform's image storage, tagged with the build number and git commit SHA."}
                            {currentStage === 3 &&
                              "Your app is being deployed with automatic scaling, traffic routing, and revision history."}
                            {currentStage >= 4 &&
                              "Your application is now live! It will automatically scale up under traffic and sleep when idle to save resources."}
                          </Typography>
                        </Box>
                      </motion.div>
                    </AnimatePresence>

                    {/* App Info Card */}
                    {appData && (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          background: isDarkMode ? "#0f172a" : "#f8fafc",
                          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: isDarkMode ? "#64748b" : "#94a3b8",
                            mb: 1,
                          }}
                        >
                          Application Details
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: isDarkMode ? "#64748b" : "#94a3b8",
                              }}
                            >
                              Git Repository
                            </Typography>
                            {(() => {
                              const gitUrl =
                                buildData?.spec?.source?.git?.url ||
                                appData?.build?.gitUrl;
                              return gitUrl ? (
                                <Typography
                                  component="a"
                                  href={gitUrl}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "#F97316",
                                    textDecoration: "underline",
                                    textUnderlineOffset: "3px",
                                    wordBreak: "break-all",
                                    "&:hover": { textDecorationThickness: "2px" },
                                  }}
                                >
                                  {gitUrl}
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "#F97316",
                                  }}
                                >
                                  N/A
                                </Typography>
                              );
                            })()}
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: isDarkMode ? "#64748b" : "#94a3b8",
                              }}
                            >
                              Build Count
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: isDarkMode ? "#e2e8f0" : "#1e293b",
                              }}
                            >
                              {buildData?.status?.buildCounter || appData?.build?.buildCounter || 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: isDarkMode ? "#64748b" : "#94a3b8",
                              }}
                            >
                              Latest Image
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: isDarkMode ? "#e2e8f0" : "#1e293b",
                                maxWidth: 300,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {buildData?.status?.latestImage || appData?.build?.latestImage || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </>
                )}

                {/* Tab 1: Build Logs */}
                {activeTab === 1 && (
                  <Box sx={{ display: "flex", gap: 2, height: "calc(70dvh - 100px)", overflow: "hidden" }}>
                    {/* Builds List */}
                    {showBuildsList && (
                      <Box
                        component={motion.div}
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 240, opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        sx={{
                          borderRadius: "16px",
                          background: isDarkMode ? "#0f172a" : "#f8fafc",
                          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                          overflow: "hidden",
                          flexShrink: 0,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            borderBottom: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <History
                              sx={{
                                fontSize: "1rem",
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: isDarkMode ? "#e2e8f0" : "#1e293b",
                              }}
                            >
                              Builds
                            </Typography>
                            <Chip
                              size="small"
                              label={builds.length}
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                background: isDarkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.05)",
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                              }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            onClick={fetchBuilds}
                            disabled={isLoadingBuilds}
                            sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
                          >
                            <Refresh
                              fontSize="small"
                              sx={{
                                animation: isLoadingBuilds
                                  ? "spin 1s linear infinite"
                                  : "none",
                                "@keyframes spin": {
                                  "0%": { transform: "rotate(0deg)" },
                                  "100%": { transform: "rotate(360deg)" },
                                },
                              }}
                            />
                          </IconButton>
                        </Box>
                        <List
                          sx={{
                            p: 1,
                            maxHeight: "calc(70dvh - 180px)",
                            overflowY: "auto",
                            overscrollBehavior: "contain",
                          }}
                        >
                          {isLoadingBuilds && builds.length === 0 ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : builds.length === 0 ? (
                            <Typography
                              sx={{
                                textAlign: "center",
                                color: isDarkMode ? "#64748b" : "#94a3b8",
                                fontSize: "0.85rem",
                                p: 3,
                              }}
                            >
                              No builds found
                            </Typography>
                          ) : (
                            builds.map((build) => (
                              <BuildHistoryItem
                                key={build.metadata.uid}
                                build={build}
                                isSelected={
                                  selectedBuild?.metadata?.uid === build.metadata.uid
                                }
                                onClick={() => setSelectedBuild(build)}
                                isDarkMode={isDarkMode}
                              />
                            ))
                          )}
                        </List>
                      </Box>
                    )}

                    {/* Logs Panel */}
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
                      {/* Toggle builds list */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => setShowBuildsList(!showBuildsList)}
                          sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
                        >
                          {showBuildsList ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                        {selectedBuild && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: isDarkMode ? "#e2e8f0" : "#1e293b",
                              }}
                            >
                              Build #
                              {selectedBuild?.metadata?.labels?.[
                                "image.kpack.io/buildNumber"
                              ] || "N/A"}
                            </Typography>
                            {(() => {
                              const cond = selectedBuild?.status?.conditions?.[0];
                              const reason = cond?.reason || "";
                              const label =
                                cond?.status === "True"
                                  ? "Success"
                                  : reason === "BuildRunning"
                                  ? "Building"
                                  : reason === "PodInitializing" || cond?.status === "Unknown" || /notready/i.test(reason)
                                  ? "Preparing"
                                  : cond?.status === "False"
                                  ? "Failed"
                                  : "Pending";
                              const palette = {
                                Success: { bg: "rgba(34, 197, 94, 0.15)", fg: "#22c55e" },
                                Building: { bg: "rgba(245, 158, 11, 0.15)", fg: "#f59e0b" },
                                Preparing: { bg: "rgba(99, 102, 241, 0.15)", fg: "#6366f1" },
                                Failed: { bg: "rgba(239, 68, 68, 0.15)", fg: "#ef4444" },
                                Pending: { bg: "rgba(100, 116, 139, 0.15)", fg: "#64748b" },
                              }[label];
                              return (
                            <Chip
                              size="small"
                              label={label}
                              sx={{
                                fontSize: "0.65rem",
                                height: 20,
                                background: palette.bg,
                                color: palette.fg,
                              }}
                            />
                              );
                            })()}
                          </Box>
                        )}
                      </Box>

                      {/* Pipeline Steps */}
                      {selectedBuild && buildLogs.length > 0 && (
                        <PipelineStepsPanel
                          logs={buildLogs}
                          currentStep={-1}
                          isDarkMode={isDarkMode}
                          selectedStep={selectedStep}
                          onStepClick={setSelectedStep}
                          buildCompleted={selectedBuild?.status?.conditions?.[0]?.status === "True"}
                        />
                      )}

                      {/* Terminal */}
                      {isLoadingLogs ? (
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isDarkMode ? "#0f172a" : "#1e293b",
                            borderRadius: "12px",
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : selectedBuild ? (
                        <Box sx={{ flex: 1, minHeight: 300 }}>
                          <TerminalLog
                            logs={filteredLogs}
                            isStreaming={
                              selectedBuild?.status?.conditions?.[0]?.reason ===
                              "BuildRunning"
                            }
                            buildName={selectedBuild?.metadata?.name}
                            height="350px"
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isDarkMode ? "#0f172a" : "#1e293b",
                            borderRadius: "12px",
                            color: "#64748b",
                          }}
                        >
                          Select a build to view logs
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Tab 2: App Runtime */}
                {activeTab === 2 && (
                  <RuntimePanel
                    runtime={runtimeData}
                    build={buildInfo}
                    logs={runtimeLogs}
                    isLoadingLogs={isLoadingRuntimeLogs}
                    onRefreshLogs={() => fetchRuntimeLogs(true)}
                  />
                )}

                {/* Tab 3: Bindings — Daari services injected via C2A_SYSTEM_ENV */}
                {activeTab === 3 && (
                  <BindingsPanel
                    appName={appData?.displayName || appData?.name?.replace(/-image$/, "")}
                    showError={showError}
                    showSuccess={showSuccess}
                    currentUser={currentUser}
                    onNavigateToDaari={(path, k8sName) => {
                      onClose?.();
                      if (path && k8sName) {
                        window.location.href = `/daari/${encodeURIComponent(path)}/${encodeURIComponent(k8sName)}`;
                      } else {
                        window.location.href = "/daari";
                      }
                    }}
                  />
                )}

                {/* Tab 4: Metrics */}
                {activeTab === 4 && (
                  <MetricsPanel
                    service={{
                      metadata: { name: runtimeData?.serviceName || appData?.name },
                      displayName: appData?.displayName || buildData?.metadata?.displayName,
                    }}
                  />
                )}

                {/* Tab 5: Domains */}
                {activeTab === 5 && (
                  <DomainsPanel
                    appName={appData?.displayName || appData?.name?.replace(/-image$/, "")}
                    projectId={currentGroup?.projectId}
                    showError={showError}
                  />
                )}

                {/* Tab 6: Showcase */}
                {activeTab === 6 && (
                  <ShowcasePanel
                    imageUid={buildData?.metadata?.uid || appData?._raw?.build?.metadata?.uid}
                    appName={appData?.displayName || appData?.name?.replace(/-image$/, "")}
                    showError={showError}
                  />
                )}
              </Box>
            </Box>
          )}
        </AnimatePresence>
      </Box>
    </Modal>
  );
}
